import { useCallback, RefObject } from 'react';

interface ImaConfig {
  adTagUrl?: string;
  adsRenderingSettings?: any;
}

interface UseAdsInitializationProps {
  imaConfig?: ImaConfig;
  autoPlay: boolean;
  playerRef: RefObject<any>;
  videoRef: RefObject<HTMLVideoElement>;
  uiRef: RefObject<any>;
  setupAdEventListeners: () => void;
  loadManifest: () => Promise<void>;
}

/**
 * Hook for Google IMA SDK ads initialization with Shaka Player.
 * 
 * CRITICAL TIMING REQUIREMENT:
 * This hook must be called LAST in the initialization sequence (after player, System73, and UI).
 * The manifest must NOT be loaded before initializeAds() is called, otherwise IMA cannot
 * intercept playback for pre-roll ads.
 * 
 * INITIALIZATION FLOW:
 * 1. Shaka Player is created (with hasAds=true, skipping auto-load)
 * 2. System73 wraps player (if configured)
 * 3. Shaka UI is initialized
 * 4. This initializeAds() is called:
 *    a. Sets up IMA SDK with ad container from Shaka UI
 *    b. Requests ads from VAST tag (async)
 *    c. Waits for VAST to load
 *    d. Loads manifest (IMA intercepts to play pre-roll)
 * 
 * See ARCHITECTURE.md for complete initialization sequence details.
 */
export const useAdsInitialization = ({
  imaConfig,
  autoPlay,
  playerRef,
  videoRef,
  uiRef,
  setupAdEventListeners,
  loadManifest
}: UseAdsInitializationProps) => {
  const initializeAds = useCallback(async () => {
    if (!imaConfig?.adTagUrl || !playerRef.current || !videoRef.current || !uiRef.current) {
      return;
    }

    if (!window.google?.ima) {
      console.error('Google IMA SDK not available when trying to initialize ads');
      return;
    }

    try {
      const player = playerRef.current;
      const video = videoRef.current;
      const ui = uiRef.current;

      // Get the proper ad container from Shaka UI controls
      const controls = ui.getControls();
      const container = controls.getClientSideAdContainer();

      const adManager = player.getAdManager();
      if (!adManager) {
        console.error('Ad manager not available');
        return;
      }

      const google = window.google;

      // Configure ads rendering settings
      // When autoplay is enabled, we need to restore playback state after ad breaks
      // so the content resumes automatically after the ad completes
      let adsRenderingSettings = null;
      if (imaConfig.adsRenderingSettings) {
        adsRenderingSettings = imaConfig.adsRenderingSettings;
      } else if (autoPlay) {
        adsRenderingSettings = new (google.ima as any).AdsRenderingSettings();
        adsRenderingSettings.restoreCustomPlaybackStateOnAdBreakComplete = true;
      }

      // STEP 1: Initialize IMA SDK with Shaka's ad container and video element
      // Must be called before requestClientSideAds() to set up the rendering environment
      adManager.initClientSide(container, video, adsRenderingSettings);

      // STEP 2: Request ads from the VAST tag URL (async operation)
      // This initiates the VAST XML fetch but doesn't block
      const adsRequest = new google.ima.AdsRequest();
      adsRequest.adTagUrl = imaConfig.adTagUrl;
      adManager.requestClientSideAds(adsRequest);

      // STEP 3: Setup event listeners to track ad lifecycle
      setupAdEventListeners();

      // STEP 4: Wait for VAST XML to load
      // Without this delay, the manifest might load before VAST is ready,
      // preventing IMA from intercepting playback for pre-roll ads
      await new Promise(resolve => setTimeout(resolve, 1000));

      // STEP 5: Load the manifest (finally!)
      // At this point, IMA is ready and will intercept playback to show pre-roll ads
      // This is why useShakaPlayer skips auto-load when hasAds=true
      await loadManifest();
    } catch (error) {
      console.error('Error initializing ads:', error);
    }
  }, [imaConfig, autoPlay, playerRef, videoRef, uiRef, setupAdEventListeners, loadManifest]);

  return { initializeAds };
};
